import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Button,
  CardBody,
  CardFooter,
  CardHeader,
  Divider,
  Heading,
  Icon,
  Stack,
  Text,
  Tooltip,
} from '@chakra-ui/react';
import { CheckCircleIcon, ExclamationCircleIcon, PlusIcon } from '@heroicons/react/outline';
import { useRef, useState } from 'react';
import { useTranslations } from 'use-intl';

import { DraftAgent } from '@blockpulse3/graphql/hooks';
import { useExpandAccordion } from '@blockpulse3/ui/ui-hooks';

import { OtherRoleForm, draftAgentTitle } from '../../../../OtherRoleForm';
import { useSPVForm } from '../../provider';
import { draftAgentDefaultValues, schemaAgent } from '../../provider/schema';

type Props = {
  /* ** Back step callback ** */
  onCancel: () => void;
  /* ** Next step callback ** */
  onSubmit: (data: DraftAgent[]) => void;
};

/**
 * OtherRoleStep.
 * Step that renders the form for the other roles of the company.
 * It is splitted on purpose, to keep the form agnostic of useLegalIdentityForm().
 *
 * @param {Props}
 * @returns {JSX.Element}
 */
export function OtherRoleStep({ onCancel, onSubmit }: Props): JSX.Element {
  const formRef = useRef<HTMLFormElement>(null);

  const i18nGeneral = useTranslations('General');
  const i18nRoleValues = useTranslations('RoleValues');
  const i18nOtherRoleStep = useTranslations('OtherRoleStep');

  const { companyDraftContent } = useSPVForm();
  const { agents } = companyDraftContent;

  const [records, setRecords] = useState<DraftAgent[]>(agents);
  /* ** Controls expended accordions ** */
  const { indexes, toggle, expand, collapse } = useExpandAccordion(
    /* ** Close all accordion by default ** */
    Object.keys(agents).reduce((acc, cur) => ({ ...acc, [cur]: [] }), {}),
  );

  /* ** Update te state of the expanded accordion by its id ** */
  const handleIndexChange = (id: number): void => {
    toggle(id.toString());
  };

  /* ** Returns other role record, go to next step ** */
  const handleStepSubmit = (): void => {
    formRef.current?.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
    const valid = records.every((role) => {
      return schemaAgent.isValidSync(role);
      // if (role.type === IdentityType.COMPANY) {
      //   return schemaAgent.isValidSync(role);
      // }
      // return schemaAgent.isValidSync(role);
    });
    if (valid) {
      onSubmit(records);
    }
  };

  /* ** Cancel, go back to the previous step ** */
  const handleStepCancel = (): void => {
    onCancel();
  };

  /* ** Fill an other role entry in the record ** */
  const handleAddOtherRole = (id: number, data: DraftAgent): void => {
    setRecords((prevRecords) => {
      const newRecords = [...prevRecords];
      newRecords[id] = data;
      return newRecords;
    });
    collapse(id.toString());
  };

  /* ** Add a new other role form, with default values ** */
  const handleNewOtherRoleForm = (): void => {
    expand(records.length.toString());
    setRecords((prevRecords) => [...prevRecords, draftAgentDefaultValues]);
  };

  /* ** Delete an other role form using the generated id ** */
  const handleDeleteOtherRole = (id: number): void => {
    setRecords((prevRecords) => {
      const newRecords: DraftAgent[] = [];
      prevRecords.forEach((rec, index) => {
        if (index !== id) {
          newRecords.push(rec);
        }
      });
      return newRecords;
    });
  };

  /* ** i18n version of otherRoleTitle for roles ** */
  const i18nOtherRoleTitle = (rep: DraftAgent): string => {
    if (schemaAgent.isValidSync(rep)) {
      return i18nRoleValues(rep.role as keyof IntlMessages['RoleValues']);
    }
    return i18nOtherRoleStep('OtherRoleTitle');
  };

  /* ** Fill role record with identity values on natural switch toggle ** */
  const handleToggleNaturalSwitch = (id: number, isNatural: boolean): void => {
    // if (isNatural && mainIdentity) {
    //   const date = dayjs(mainIdentity?.birthdate).format('YYYY-MM-DD');
    //   /* ** Set state with identity values from the workspace when the switch is on ** */
    //   setRoles((prevRoles) => ({
    //     ...prevRoles,
    //     [id]: {
    //       isLegal: false,
    //       quality: {
    //         ...prevRoles[id].quality,
    //         firstName: mainIdentity.firstName,
    //         birthdate: date,
    //         lastName: mainIdentity.lastName,
    //       },
    //     },
    //   }));
    // } else {
    //   /* ** Set state with schema default values when the switch is off ** */
    //   setRoles((prevRoles) => ({ ...prevRoles, [id]: otherRolesDefaultValues }));
    // }
  };

  /* ** Fill role record with default legal values on legal switch toggle ** */
  const handleToggleLegalSwitch = (id: number): void => {
    setRecords((prevRecords) => {
      const newRecords = [...prevRecords];
      newRecords[id] = draftAgentDefaultValues;
      return newRecords;
    });
  };

  return (
    <>
      <CardHeader as={Stack} spacing="2">
        <Heading size="lg">{i18nOtherRoleStep('Title')}</Heading>
        <Text color="gray.600" fontSize="lg">
          {i18nOtherRoleStep('SubTitle')}
        </Text>
      </CardHeader>
      <Divider />
      <CardBody as={Stack} spacing="4">
        {records.map((record, id) => (
          <Accordion
            key={id}
            allowToggle
            index={indexes[id]}
            onChange={(): void => handleIndexChange(id)}
          >
            <AccordionItem mt="0">
              <AccordionButton>
                {schemaAgent.isValidSync(record) ? (
                  <>
                    <Tooltip hasArrow label={i18nOtherRoleStep('Tooltip.Success')} placement="top">
                      <Icon as={CheckCircleIcon} boxSize="30" color="green.500" />
                    </Tooltip>
                    <Box flex="1" p="2" textAlign="left">
                      <Heading fontSize="2xl">{draftAgentTitle(record)}</Heading>
                      <Text>{record.email}</Text>
                    </Box>
                  </>
                ) : (
                  <>
                    <Tooltip hasArrow label={i18nOtherRoleStep('Tooltip.Warning')} placement="top">
                      <Icon as={ExclamationCircleIcon} boxSize="30" color="yellow.400" />
                    </Tooltip>
                    <Box flex="1" p="2" textAlign="left">
                      <Heading fontSize="2xl">{i18nOtherRoleTitle(record)}</Heading>
                    </Box>
                  </>
                )}
                <AccordionIcon boxSize="8" />
              </AccordionButton>
              <AccordionPanel>
                <OtherRoleForm
                  defaultValues={record}
                  ref={formRef}
                  onCancel={(): void => handleDeleteOtherRole(id)}
                  onSubmit={(data): void => handleAddOtherRole(id, data)}
                  onToggleLegalSwitch={(): void => handleToggleLegalSwitch(id)}
                  onToggleNaturalSwitch={(isNatural): void =>
                    handleToggleNaturalSwitch(id, isNatural)
                  }
                />
              </AccordionPanel>
            </AccordionItem>
          </Accordion>
        ))}
        <Button
          fontSize="2xl"
          justifyContent="start"
          leftIcon={<PlusIcon height="25" width="25" />}
          variant="container"
          onClick={handleNewOtherRoleForm}
        >
          {i18nOtherRoleStep('AddOtherRole')}
        </Button>
      </CardBody>
      <CardFooter as={Stack} direction={{ base: 'column', md: 'row' }} spacing="4">
        <Button variant="secondary" w="full" onClick={handleStepCancel}>
          {i18nGeneral('Back')}
        </Button>
        <Button w="full" onClick={handleStepSubmit}>
          {i18nGeneral('Next')}
        </Button>
      </CardFooter>
    </>
  );
}

export type OtherRoleStepProps = Props;
